import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { GridValueFormatterParams, GridValueSetterParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import moment, { Moment } from 'moment';
import { parse, stringify } from 'qs';
import { utils, read } from 'xlsx';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { v4 } from 'uuid';
import { ValidationError } from 'yup';

import { format, formatISO, isValid, setDate, sub } from 'date-fns';
import { IContactPhones } from '@modules/admin/AdminTools/CreateContact/queries';

import { Notistack } from './components/Notistack/Notistack';
import { dateFormat, dateFormatForDayJs, dateFormatWithTime, dateTimeZoneFormat } from './constants/formats';
import { EStipTypeEntity, EViewType, IOption, IPeriod, IStipType } from './interfaces';

interface IFormatDateOptions {
  format?: string;
  emptyPlaceholder?: string;
  getMoment?: (dateString: string) => Moment;
}

type TInputDate = string | number | null;

const SHORT_ZIP_CODE_LENGTH = 5;

export const getMoment = (dateString?: TInputDate): Moment => moment(dateString, true);

export const getDateOnly = (dateString: string): string => (dateString ? dateString.substring(0, 10) : '');

export const momentDateDifference = (startDateString: string, endDateString: string): number => {
  const startDate = dayjs(getDateOnly(startDateString));
  const endDate = dayjs(getDateOnly(endDateString));
  return startDate.diff(endDate, 'd');
};

export const getMomentWithInputFormat = (dateString?: TInputDate, incomingFormat?: string) => moment(dateString, incomingFormat);

export const formatDate = (inputDate?: TInputDate, options?: IFormatDateOptions, format = dateFormat): string => {
  const resultOptions = {
    format,
    emptyPlaceholder: '—',
    getMoment,
    ...options,
  };

  if (!inputDate) {
    return resultOptions.emptyPlaceholder;
  }

  const dateMoment = resultOptions.getMoment(inputDate as string);
  
  return dateMoment.format(resultOptions.format);
};

export const formatSecondsToMinutesAndSeconds = (seconds: number | string): string => {
  const totalSeconds = typeof seconds === 'string' ? parseInt(seconds, 10) : seconds;

  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '—';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes === 0 && remainingSeconds === 0) {
    return '—';
  }

  if (minutes === 0 && remainingSeconds > 0) {
    return `${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`;
  }

  let result = '';

  if (minutes > 0) {
    result += `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  if (remainingSeconds > 0) {
    if (result.length > 0) {
      result += ' ';
    }
    result += `${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`;
  }

  return result;
};

export const replaceDateWithUserTimeZone = (text: string | null) => {
  const datePattern = /\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}/;
  const match = text?.match(datePattern);

  if (match) {
    const userTimeZoneDate = new Date(match[0] + 'Z').toLocaleString();
    return text?.replace(match[0], userTimeZoneDate);
  }

  return text;
};

export const formatPhoneNumber = (phoneNumber?: string): string | undefined => {
  const cleaned = phoneNumber?.replace(/\D+/g, '') || '';

  const fullPhoneRegex = new RegExp(/(\d{1})(\d{3})(\d{3})(\d{4})/);

  let match = fullPhoneRegex.exec(cleaned);

  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }

  const shortPhoneRegex = new RegExp(/(\d{3})(\d{3})(\d{4})/);

  match = shortPhoneRegex.exec(cleaned);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber;
};

export const formatZipcode = (zipcode: string) => {
  const digitsOnly = zipcode?.replace(/\D/g, '');

  if (digitsOnly?.length > SHORT_ZIP_CODE_LENGTH) {
    return digitsOnly.slice(0, SHORT_ZIP_CODE_LENGTH) + '-' + digitsOnly.slice(5);
  }
  return digitsOnly;
};

const phoneRegex = new RegExp(/([2-9]\d{2})(\d{3})(\d{4})/);

export const formatCurrency = (value?: number | string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format((value || '0') as number);
};

export const formatPercentage = (value?: number) => {
  return `${value?.toFixed(2)}%`;
};

export const formatNumberValue =
  (prefix = '', suffix = '', nullable = false) =>
  ({ value }: GridValueFormatterParams) => {
    if ((value === null || value === undefined) && nullable) {
      return null;
    }

    const formatted = formatCurrency(value);

    if (prefix === '$') {
      return formatted;
    }

    return !isNaN(value) ? prefix + formatted.replace('$', '') + suffix : null;
  };

export const formatToNumber = (value?: string) => {
  return Number(value?.replace(/[^0-9.-]+/g, ''));
};

export const roundValue = (field: string) => (params: GridValueSetterParams) => ({
  ...params.row,
  [field]: Math.round(params.value),
});

export const phoneNumberIsValid = (phone: string) => phoneRegex.test(phone);

const zipCodeRegex = new RegExp(/^\d{5}-?(\d{4})?$/);

export const zipCodeIsValid = (zipCode: string | null) => !!zipCode && zipCodeRegex.test(zipCode);

export const deCamelize = (word: string | number | symbol = '') => {
  word = String(word);
  const wordRegex = /[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g;
  const [firstWord = '', ...restWords] = word.match(wordRegex) || [];
  return [
    firstWord?.[0]?.toUpperCase() + firstWord?.substring(1),
    ...restWords.map((w) => w?.[0].toUpperCase() + w?.substring(1)),
  ].join(' ');
};

export const gridDateFormatter = ({ value }: GridValueFormatterParams, ignoreTimeZone?: boolean) =>
  formatDate(ignoreTimeZone && value ? dateWithoutTimeZone(value) : (value as string));

export const gridDateTimeFormatter = ({ value }: GridValueFormatterParams) =>
  formatDate(value as string, { format: dateFormatWithTime });

export const mapGridIndexes = (
  list?: any[] | null // eslint-disable-line
) =>
  list?.map?.((item, index) => ({
    ...item,
    id: item?.id || index,
  })) || [];

export const newGridRecordId = 'temp';
export const newGridRecord = {
  id: newGridRecordId,
  isNew: true,
  createdDate: new Date().toISOString(),
  updatedDate: new Date().toISOString(),
};

export const dateRegex = new RegExp(/^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d$/);
export const timeRegex = new RegExp(/^(0[0-9]|1[012]):[0-5][0-9](\s)?(am?|pm?)$/, 'i');

export const queryParams = (): any => {
  // eslint-disable-line
  return parse(window.location.search.replace('?', ''));
};

export const withQueryParams = (route: string, extraParams: any = {}, includeUrlParams = true) => {
  // eslint-disable-line
  const params = stringify({
    ...(includeUrlParams && queryParams()),
    ...extraParams,
  });

  return `${route}?${params}`;
};

export const initializeValues = (obj: { excludedStates: string[]; includedZipCodes: string; includedContactIds: string }) =>
  JSON.parse(JSON.stringify(obj, (_k, v) => (v === null || v === undefined ? '' : v)));

export const filterJSON = <T>(obj: T) => JSON.stringify(obj, (_k, v) => v !== null && v !== undefined && v);

export const filterEmptyStrings = <T>(obj: T) => JSON.parse(JSON.stringify(obj, (_k, v) => (v === '' ? undefined : v)));

export const randomNumber = (floor: number, ceil: number) => Math.ceil(Math.random() * (ceil - floor) + floor);

export const scrollToErrorInput = () => {
  setTimeout(() => {
    const errorField = document.getElementsByClassName('Mui-error')?.[0];

    if (errorField) {
      scrollIntoView(errorField, {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'center',
      });
    }
  }, 100);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = function (fn: any, time: number) {
  let timeout: string | number | NodeJS.Timeout | undefined;

  return function () {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const self = this; // eslint-disable-line
    const functionCall = function () {
      return fn.apply(self, arguments); // eslint-disable-line
    };
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
};

export const formatBytes = (bytes = 0, decimals = 2) => {
  if (!bytes) {
    return '0 Bytes';
  }

  const kb = 1024;
  const sizes = ['Bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
  const i = Math.floor(Math.log(bytes) / Math.log(kb));

  return `${parseFloat((bytes / Math.pow(kb, i)).toFixed(decimals))} ${sizes[i]}`;
};

export const toBase64 = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const urlToFile = async (url: string, filename: string, mimeType: string) => {
  return fetch(url)
    .then(function (res) {
      return res.arrayBuffer();
    })
    .then(function (buf) {
      return new File([buf], filename, { type: mimeType });
    });
};

export const base64ToUint8Array = (base64: string) => {
  const charCodeAt = (char: string) => char.charCodeAt(0);

  return new Uint8Array(atob(base64).split('').map(charCodeAt));
};

export const handleDownloadPdf = (blobPart: Uint8Array, fileName: string) => {
  const blob = new Blob([blobPart], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export function toggleListItem<T>(list: T[] = [], item: T, remove?: boolean) {
  const index = list?.indexOf(item);

  if (index >= 0) {
    const copy = [...list];
    copy?.splice(index, 1);
    return copy;
  } else if (!remove) {
    return [...list, item];
  }

  return list;
}

export function getListsDifference<T>(source: T[] = [], dest: T[] = [], key: keyof T, additionalProp = 'isDeleted') {
  const sourceSet = new Set(source.map((item) => item[key]));
  const destSet = new Set(dest.map((item) => item[key]));
  const result: T[] = [];

  [...source, ...dest].forEach((item) => {
    const existsInSource = sourceSet.has(item[key]);
    const existsInDest = destSet.has(item[key]);

    if (existsInSource && !existsInDest) {
      result.push({ ...item, [additionalProp]: true });
    } else if (!existsInSource && existsInDest) {
      result.push({ ...item, [additionalProp]: false });
    }
  });

  return result;
}

export const openPdf = (base64Str: string, fileName?: string) => {
  // Do not remove this for now need to test on server before finalizing.
  // let pdfWindow: Window = window.open('') as Window;
  // pdfWindow.document.write(
  //   "<iframe width='100%' height='100%' src='data:application/pdf;charset=utf-16le;base64," + encodeURI(base64Str) + "'></iframe>"
  // );
  // fileName will displayed in tab title bar
  try {
    openFile(base64Str, 'pdf', fileName);
  } catch {
    Notistack.generalError('Pop-ups blocked. Please enable them and try again.');
  }
};

export const openPdfByUrl = (pdfUrl: string) => {
  const link = document.createElement('a');
  link.setAttribute('href', pdfUrl);
  link.setAttribute('target', '_blank');
  const event = new MouseEvent('click');
  link.dispatchEvent(event);
};

export const downloadAsData = async (name: string, base64Str: string | undefined | null, blobDownloadLink?: string | undefined | null) => {
  
  if (blobDownloadLink) {
    try {
      const response = await fetch(blobDownloadLink);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name); 
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    } catch (error) {
      console.error('Download failed, falling back to new tab', error);
      // If fetch fails (CORS), fallback to opening in new tab
      window.open(blobDownloadLink, '_blank');
      return;
    }
  }
  
  const element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;base64,${base64Str}`);
  element.setAttribute('download', name);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

/**
 * TO downloaded a base 64 encoded file
 * @param filename name of file t be downloaded
 * @param content base74 encoded string from API
 * @param contentType application/json, 'application/zip', 'application/pdf' etc
 */
export const downloadBase64File = (filename: string, content: string, contentType: string) => {
  const uriContent = URL.createObjectURL(new Blob([base64ToByteArray(content)], { type: contentType }));
  const link = document.createElement('a');
  link.setAttribute('href', uriContent);
  link.setAttribute('download', filename);
  const event = new MouseEvent('click');
  link.dispatchEvent(event);
};

export const base64ToByteArray = (base64String: string | undefined | null) => {
  if (!base64String) {
    throw new Error('Invalid base64 string');
  }
  const raw = window.atob(base64String);
  const rawLength = raw.length;
  const array = new Uint8Array(new ArrayBuffer(rawLength));
  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
};

export const openFile = (base64Str: string | undefined | null, ext: string, fileName?: string, imageLink?: string) => {

  if (imageLink) {
    window.open(imageLink, '_blank');
    return;
  }

  // meta-data property 'title' of file will be displayed in print popup. And it can't be changed
  fileName = fileName ? fileName : 'File' + ext.toUpperCase();
  let contentType = 'application/octet-stream';

  if (ext.toLowerCase() === 'pdf') {
    contentType = 'application/pdf';
  } else if (['jpeg', 'jpg', 'png', 'gif'].includes(ext.toLowerCase())) {
    contentType = 'image/' + ext.toLowerCase();
  } else if (ext.toLocaleLowerCase() === 'html') {
    contentType = 'text/html';
  }

  const pdfWindow: Window = window.open('') as Window;
  const uriContent = URL.createObjectURL(new Blob([base64ToByteArray(base64Str)], { type: contentType }));
  pdfWindow.document.write(
    `
    <html><head><title>${fileName}</title></head><body height="100%" width="100%">
      <iframe width='100%' height='100%' src='${uriContent}'>
      </iframe>
    </body></html>
    `
  );
  pdfWindow.document.title = fileName;
  // window.open("data:application/octet-stream;charset=utf-16le;base64,"+base64Str);
};
export const openHtmlFile = (htmlString: string, ext: string, fileName?: string) => {
  // meta-data property 'title' of file will be displayed in print popup. And it can't be changed
  try {
    const requestFileName = fileName || 'File' + ext.toUpperCase();
    const browserWindow: Window = window.open('') as Window;
    browserWindow.document.write(htmlString);
    browserWindow.document.title = requestFileName;
  } catch (err) {
    const {
      response: {
        data: { message },
      },
    } = err as { response: { data: { message: string } } };
    Notistack.generalError(`Browser blocked the request or html file is corrupted - ${message}`);
  }
};

export const formatValidation = (validationErrors: ValidationError[]) => {
  const formattedObject = validationErrors.reduce((acc, cur) => {
    if (cur.path) {
      return {
        ...acc,
        [cur.path]: cur.message,
      };
    }

    return acc;
  }, {});

  return formattedObject;
};

export const calculateAllMonths = <T extends { yrs: number | null; mths: number | null }, K extends keyof T>(
  obj: T,
  field: K
): T[K] | number => {
  if (obj.yrs !== undefined || obj.mths !== undefined) {
    const years = obj.yrs ? obj.yrs : 0;
    const months = obj.mths ? obj.mths : 0;

    return years * 12 + months;
  }
  return obj[field];
};

export const formatString = (text: string, convertToLower = true) =>
  text
    ? text
        ?.split(' ')
        ?.map((str) => str.charAt(0).toUpperCase() + (convertToLower ? str.slice(1).toLowerCase() : str.slice(1)))
        ?.join(' ')
    : '-';

export const nameRegex = new RegExp(/^[a-zA-Z ]*$/);

export const nameIsValid = (name: string | null) => !!name && nameRegex.test(name);

export const maxError = (value: string, max: number) => value?.length <= max;

export const getMonthCount = (address: { years: number; months: number }) => (address.years || 0) * 12 + (address.months || 0);

export const getOptions = <T>(
  data: T[] = [],
  valueKey: keyof T = 'id' as keyof T,
  labelKey: keyof T = 'name' as keyof T,
  groupKey?: keyof T
): IOption[] =>
  data?.map((item) => {
    const option: IOption = {
      value: `${item[valueKey]}`,
      label: `${item[labelKey]}`,
    };

    if (groupKey) {
      option.groupLabel = `${item[groupKey]}`;
    }

    return option;
  }) ?? [];

/**
 * This function is use to validate email
 * @param email
 * @returns Boolean
 */
export const validateEmail = (email: string) => {
  const expression = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  return expression.test(String(email).toLowerCase());
};

export const isValidFile = (file: File, extensions: string[]): boolean => {
  if (file?.size <= 0) return false;
  else if (file?.name.length > 0) {
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    return extensions.includes(fileExtension);
  }
  return false;
};

export const splitCamelCase = (word: string) => {
  const matches = word.match(/[A-Z][a-z]+/g);

  if (matches) {
    return matches.join(' ');
  }

  return word;
};

export const areFieldsMatched = (currentField: string, ocrField: string) => {
  return currentField.toLowerCase().trim().replaceAll('-', '') === ocrField.toLowerCase().trim().replaceAll('-', '');
};

export const handleCheckMatchedFields = (currentField: string, ocrField: string) => {
  return areFieldsMatched(currentField, ocrField) ? 'Match' : 'Mismatch';
};

export const getTabNumber = (query: string) => {
  const match = query.match(/(?<=\?tab=)\d+/);

  if (match) {
    return parseInt(match[0], 10) + 1;
  }

  return 0;
};

export const filteredStipType = (stipTypeArr?: IStipType[], viewType?: string) => {
  if (!stipTypeArr) return [];

  const filtered = stipTypeArr
    .filter((stipType) => stipType.isHidden !== true)
    .sort((firstStipType: IStipType, secondStipType: IStipType) => {
      // need to keep stip type with  Other at end
      if (firstStipType.name.includes('Other')) return 1;
      if (secondStipType.name.includes('Other')) return -1;
      return firstStipType.name.localeCompare(secondStipType.name);
    });

  switch (viewType) {
    case EViewType.STIP_REQUEDST_MODAL:
      return filtered.filter(
        (stipType) =>
          !stipType.isRequired &&
          stipType.willComeFromCustomer &&
          [EStipTypeEntity.CONTACT, EStipTypeEntity.ASSET].includes(stipType.relatedToEntity)
      );
    case EViewType.FILES_TAB:
      return filtered.filter((stipType) => stipType.relatedToEntity !== EStipTypeEntity.DEAL);
    default:
      return filtered;
  }
};

export const getValue = <T>(data: T, key: keyof T) => data[key] ?? '—';

export const parseDate = (str: string) => {
  return moment(str, dateTimeZoneFormat).valueOf();
};

export const datediff = (first: number, second: number) => {
  const startDate = moment(first);
  const endDate = moment(second);
  return endDate.diff(startDate, 'days');
};

export const trimCreditCardNumber = (cardNumber: string) => cardNumber.replace(/\s/g, '');

export const isAtLeastOnePhoneProvided = (phones: IContactPhones) =>
  !!(phones?.homePhone || phones?.workPhone || phones?.cellPhone);

export const initiateWSConnection = async (url: string) => {
  const negotiationResponse = await fetch(url + '/negotiate', { method: 'POST' });
  const info = await negotiationResponse.json();

  return new HubConnectionBuilder()
    .withUrl(info.url, { accessTokenFactory: () => info.accessToken })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Information)
    .build();
};

export const mapGridIds = (
  list?: any[] | null // eslint-disable-line
) =>
  list?.map?.((item) => {
    const id = v4() as string;

    return {
      ...item,
      id: item?.id || id,
    };
  }) || [];

export const getDiffYearsMonths = (dateValue: string) => {
  if (!dateValue) {
    return [0, 0];
  }

  const isFuture = new Date(dateValue) > new Date();

  if (isFuture) {
    return [0, 0];
  }

  const startDate = moment(new Date(dateValue).setDate(0));
  const currentMonthDefaultDate = moment(new Date().setDate(1));
  const diffYears = currentMonthDefaultDate.diff(startDate, 'years');
  const diffMonths = currentMonthDefaultDate.diff(startDate, 'months') % 12;

  return [diffYears, diffMonths];
};

export const getDateByYearsMonths = (years: number, months: number, formatToISO = false) => {
  const updatedDate = sub(setDate(new Date(), 1), { years, months });
  const isDateValid = isValid(updatedDate);

  if (!isDateValid) {
    return '';
  }

  return formatToISO ? formatISO(updatedDate) : format(sub(setDate(new Date(), 1), { years, months }), 'MM/dd/yyyy');
};

export const base64toBlob = (base64Image: string) => {
  if (base64Image) {
    const parts = base64Image.split(';base64,');

    // Hold the content type
    const imageType = parts[0].split(':')[1];

    // Decode Base64 string
    const decodedData = atob(parts[1]);

    // Create UNIT8ARRAY of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length);

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }

    // Return BLOB image after conversion
    return new Blob([uInt8Array], { type: imageType });
  }
};

export const fedexRedirect = (trackId: string) => {
  if (trackId) {
    const url = `https://www.fedex.com/fedextrack/?tracknumbers=${trackId}&cntry_code=us`;
    window.open(url, '_blank');
  }
};

export const extractUrlTillTab = (url: string) => {
  const regex = /^(.*?)(?=&|\?tab=|$)/;
  const match = regex.exec(url);

  return match ? match[1] : null;
};

export const getPeriodOptions = (periods: IPeriod[]) => {
  const sortedPeriods = periods.sort((firstPeriod, secPeriod) => {
    const firstDate = new Date(firstPeriod.fromDate);
    const secDate = new Date(secPeriod.fromDate);

    return secDate.getFullYear() - firstDate.getFullYear() || secDate.getMonth() - firstDate.getMonth();
  });

  return getOptions(sortedPeriods, 'id', 'name');
};

export const countRowsInUploadFile = async (uploadFile: File): Promise<number> =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        const workbook = read(new Uint8Array(content), { type: 'array' });

        let totalRowCount = 0;

        workbook.SheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const rows = utils.sheet_to_json(sheet, { header: 1, blankrows: false });

          const rowCount = rows.length - 1;

          totalRowCount += rowCount;
        });

        resolve(totalRowCount);
      };

      reader.readAsArrayBuffer(uploadFile);
    } catch (error) {
      reject(error);
    }
  });

export const copyToClipboard = (value = '') => {
  navigator.clipboard.writeText(value);
  Notistack.infoMessage('Copied');
};

export const dateWithoutTimeZone = (date: string | Date) => {
  const finalDate = typeof date === 'object' ? moment(date).format(dateFormatForDayJs) : date;

  return finalDate?.split('T')[0].toString();
};

const alphaNumericRegex = /[^a-zA-Z0-9 ]/;

export const alphaNumeric = (value: string | null) => !!value && alphaNumericRegex.test(value);

export const removeExtraSpaces = (value: string | null) => value && value.replace(/\s+/g, ' ').trim();

export const extractTextFromHTML = (html: string) => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;
  return tempElement.textContent?.trim() || tempElement.innerText?.trim() || '';
};
